import { DOMUtil } from "@mcleod/core";
import { AnchoredComponentPositioner } from "../positioner/AnchoredComponentPositioner";
import { ScreenStack } from "../../components/panel/ScreenStack";
import { Tooltip } from "./Tooltip";

export class TooltipDisplayer {
    public static REMOVE_TOOLTIPS: boolean = true;
    private static scrollListenerAdded = false; // Prevents adding the listener twice when displaying nested tooltips

    /**
     * Displays the provided Tooltip
     * @param tooltip The tooltip to be displayed
     */
    static show(tooltip: Tooltip) {
        if (!this.canShowTooltip(tooltip)) {
            return;
        }
        this.initializeScrollListener();
        if (this.newTooltipIsNested(tooltip) !== true) {
            this.removeAll();
        }
        if (tooltip.anchorComponent != null) {
            tooltip.anchorComponent.tooltipInstance = tooltip;
        }
        document.body.appendChild(tooltip._element);
        if (tooltip.anchorComponent != null) {
            AnchoredComponentPositioner.autoUpdateTooltipPosition(tooltip.positionerProps).then(() => {
                tooltip.makeVisible();
            });
        } else {
            tooltip.makeVisible();
        }
        ScreenStack.pushTooltip(tooltip);
        tooltip.startTimeout();
        tooltip.shake();
    }

    private static initializeScrollListener() {
        if (!this.scrollListenerAdded) {
            Tooltip.log.debug("Adding scroll listener for the purpose of hiding all tooltips");
            const options = { capture: true, once: true };
            window.addEventListener("scroll", this.removeTooltipsOnScroll, options);
            this.scrollListenerAdded = true;
        }
    }

    private static canShowTooltip(tooltip: Tooltip): boolean {
        return tooltip != null &&
            !tooltip.isEmpty() &&
            !this.tooltipAlreadyDisplayed(tooltip) &&
            !tooltip.removedOrAnchorRemoved;
    }

    private static tooltipAlreadyDisplayed(tooltip: Tooltip): boolean {
        return ScreenStack.getAllTooltips()?.some((existingTooltip: Tooltip) => existingTooltip === tooltip);
    }

    private static newTooltipIsNested(tooltip: Tooltip): boolean {
        if (tooltip.anchorComponent == null) {
            return false;
        }
        for (const existingTooltip of ScreenStack.getAllTooltips()) {
            if (existingTooltip.isOrContains(tooltip.anchorComponent)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Removes all visible tooltips.  This is strictly a convenience method provided for clarity.
     */
    public static removeAll() {
        this.remove();
    }

    /**
     * Removes the top-most visible tooltip.
     */
    public static removeNewestTooltip() {
        this.remove(ScreenStack.getNewestTooltip());
    }

    /**
     * Removes the specified tooltip instance, or all tooltips if no single instance is provided.
     * @param tooltip The tooltip to hide.  If one is not provided, all tooltips will be hidden.
     */
    public static remove(tooltip?: Tooltip) {
        this.scrollListenerAdded = false;
        if (!this.REMOVE_TOOLTIPS) {
            return;
        }
        const oldestTooltipToHide = tooltip == null ? ScreenStack.getOldestTooltip() : tooltip;
        if (oldestTooltipToHide == null) {
            return;
        }
        const tooltipStackCopy = [...ScreenStack.getAllTooltips()];
        const tooltipToHideIndex = tooltipStackCopy.indexOf(oldestTooltipToHide);
        if (tooltip != null && tooltipToHideIndex < 0) {
            return;
        }
        for (let x = tooltipStackCopy.length - 1; x >= tooltipToHideIndex; x--) {
            const tooltip = tooltipStackCopy[x];
            if (tooltip.anchorComponent != null) {
                tooltip.anchorComponent.tooltipInstance = null;
            }
            ScreenStack.popTooltip(tooltip);
            if (tooltip.isMounted) {
                tooltip.doBeforeRemove();
                document.body.removeChild(tooltip._element);
            }
        }
    }

    /**
     * Removes all visible tooltips, unless the element being scrolled is in one of the visible tooltips.
     * @param event The scroll event
     */
    private static removeTooltipsOnScroll = (event: Event) => {
        const scrollTarget = event.target;
        const tooltipStackCopy = [...ScreenStack.getAllTooltips()];
        for (const tooltip of tooltipStackCopy) {
            if (DOMUtil.isOrContains(tooltip._element, scrollTarget as Element)) {
                Tooltip.log.debug("Not hiding tooltip(s) due to scroll that occurred inside a tooltip %o", event);
                return;
            }
        }
        Tooltip.log.debug("Hiding tooltip(s) due to scroll that occurred outside of tooltip %o", event);
        this.remove();
    }
}
